// Client du worker Pyodide léger des Reps (voir public/static/python/reps-pyodide-worker.js).
// Expose une API simple : runReps(code) -> Promise<{ stdout, stderr, ok, error, timedOut }>.
// - Worker singleton, chargé paresseusement (Pyodide ~10 Mo, une seule fois).
// - Timeout anti-boucle-infinie : au-delà de RUN_TIMEOUT_MS, on TUE le worker
//   (seul moyen d'arrêter un `while True`) et on le recrée au prochain run.
// - Sortie plafonnée (MAX_OUTPUT_CHARS) pour ne pas noyer l'UI.

const WORKER_URL = "/static/reps/python-worker.js";
const RUN_TIMEOUT_MS = 12000;
const MAX_OUTPUT_CHARS = 20000;

let worker = null;
let readyPromise = null;
let inFlight = null; // { resolve, stdout, stderr, timer }

function isAvailable() {
    return typeof window !== "undefined" && typeof Worker !== "undefined";
}

function spawn() {
    worker = new Worker(WORKER_URL);
    readyPromise = new Promise((resolve, reject) => {
        const onReady = (event) => {
            const msg = event.data || {};
            if (msg.type === "ready") {
                worker.removeEventListener("message", onReady);
                resolve();
            } else if (msg.type === "init_error") {
                worker.removeEventListener("message", onReady);
                reject(new Error(msg.text || "Pyodide init error"));
            }
        };
        worker.addEventListener("message", onReady);
        worker.addEventListener("error", (e) =>
            reject(new Error(e && e.message ? e.message : "Worker Pyodide indisponible"))
        );
    });

    worker.addEventListener("message", (event) => {
        const msg = event.data || {};
        if (!inFlight) return;
        if (msg.type === "stdout") {
            inFlight.stdout = cap(inFlight.stdout + msg.text);
        } else if (msg.type === "stderr") {
            inFlight.stderr = cap(inFlight.stderr + msg.text);
        } else if (msg.type === "done") {
            finish({ ok: true });
        } else if (msg.type === "error") {
            finish({ ok: false, error: msg.text || "Erreur d'exécution" });
        }
    });
}

function cap(s) {
    return s.length > MAX_OUTPUT_CHARS
        ? s.slice(0, MAX_OUTPUT_CHARS) + "\n[sortie tronquée]"
        : s;
}

function finish(extra) {
    if (!inFlight) return;
    const { resolve, stdout, stderr, timer } = inFlight;
    clearTimeout(timer);
    inFlight = null;
    resolve({ stdout, stderr, timedOut: false, ...extra });
}

// Retourne { stdout, stderr, ok, error?, timedOut, unavailable? }. Ne rejette
// jamais : les erreurs Python ou de timeout reviennent dans l'objet résultat.
//
// `unavailable` distingue « Python n'a pas pu démarrer » (pas de Worker, CDN
// injoignable, hors ligne) d'une vraie erreur Python. La différence compte pour
// les écrans de concept : une trace rouge peut y être VOULUE (une leçon qui
// enseigne à lire un NameError), alors qu'un runtime absent n'apprend rien et
// doit se dire autrement.
// Les appels sont SÉRIALISÉS : un écran de concept lance son exécution tout
// seul au montage, donc deux exécutions peuvent se chevaucher quand l'apprenant
// enchaîne vite (le run de l'écran quitté est encore en vol). Sans file, le
// second recevait « Une exécution est déjà en cours » et l'écran restait vide.
let queue = Promise.resolve();

// `inputs` : les réponses que la LEÇON fournit à la place de l'utilisateur.
// Pyodide ne peut pas suspendre l'exécution pour attendre une frappe ; la leçon
// dit donc d'avance ce qui est tapé, et le worker rejoue les deux gestes d'un
// vrai terminal (l'invite s'affiche, puis la réponse s'inscrit à sa suite).
export function runReps(code, inputs) {
    const next = queue.then(() => runNow(code, inputs));
    // La file ne doit jamais se rompre : runNow ne rejette pas, mais on protège
    // quand même le maillon suivant.
    queue = next.catch(() => {});
    return next;
}

async function runNow(code, inputs) {
    if (!isAvailable()) {
        return { stdout: "", stderr: "", ok: false, error: "Environnement sans Web Worker.", timedOut: false, unavailable: true };
    }
    if (!worker) spawn();
    try {
        await readyPromise;
    } catch (err) {
        // Worker mort au démarrage (CDN bloqué, etc.) : on repart de zéro au prochain appel.
        worker = null;
        readyPromise = null;
        return { stdout: "", stderr: "", ok: false, error: String(err.message || err), timedOut: false, unavailable: true };
    }
    if (inFlight) {
        return { stdout: "", stderr: "", ok: false, error: "Une exécution est déjà en cours.", timedOut: false };
    }

    return new Promise((resolve) => {
        const timer = setTimeout(() => {
            // Boucle infinie / trop long : on tue le worker, on le recréera.
            try {
                worker.terminate();
            } catch (e) {
                /* ignore */
            }
            worker = null;
            readyPromise = null;
            const { stdout, stderr } = inFlight || { stdout: "", stderr: "" };
            inFlight = null;
            resolve({
                stdout,
                stderr,
                ok: false,
                timedOut: true,
                error: "Ton code a mis trop de temps (boucle infinie ?). Exécution arrêtée.",
            });
        }, RUN_TIMEOUT_MS);

        inFlight = { resolve, stdout: "", stderr: "", timer };
        worker.postMessage({
            type: "run",
            code,
            inputs: Array.isArray(inputs) ? inputs.map((v) => String(v)) : [],
        });
    });
}

// Précharge Pyodide (à appeler quand une leçon avec python_code est ouverte).
export function warmReps() {
    if (isAvailable() && !worker) spawn();
}
